/**
 * Faanuu Board — 5x5 grid of INTERSECTIONS (nodes), not squares.
 * Pieces sit on intersections; lines connect adjacent and diagonal intersections.
 *
 * Props:
 *  - board: 5x5 array
 *  - selectedFrom: [x,y] | null
 *  - legalMovesForSelected: Move[] (full moves with .to)
 *  - mustCapturePieces: Set<string> of "x,y" of pieces that must capture
 *  - onClickIntersection: (x, y) => void
 *  - flipped: boolean (display from B's perspective)
 *  - lastMove: [{from:[x,y], to:[x,y]}] highlight
 *  - youColor: 'A'|'B'|null (for visual indicator)
 *  - animatingMove: {path, owner, king} | null
 *  - capturedCells: Array<[x,y]>
 *  - hintsOn: boolean — when false, do not show move-dots nor must-capture rings
 */
import { useMemo } from "react";
import MoveOverlay from "./MoveOverlay";
import Piece from "./Piece";
import { usePreferences } from "../context/PreferencesContext";

const SIZE = 5;

export default function Board({
  board,
  selectedFrom = null,
  legalMovesForSelected = [],
  mustCapturePieces = new Set(),
  onClickIntersection,
  flipped = false,
  lastMove = null,
  animatingMove = null,
  capturedCells = [],
  hintsOn = true,
  coachFrom = null,
  coachTo = null,
  pieceTheme: pieceThemeProp = null,
}) {
  const { pieceTheme: prefTheme } = usePreferences();
  const pieceTheme = pieceThemeProp || prefTheme;

  // We render an 8x8 grid where each cell IS an intersection.
  // Use grid layout with relative positioning; lines drawn via background and SVG diagonals.
  const validTargets = useMemo(() => {
    const set = new Set();
    for (const m of legalMovesForSelected) set.add(`${m.to[0]},${m.to[1]}`);
    return set;
  }, [legalMovesForSelected]);

  const lastFrom = lastMove ? `${lastMove.from[0]},${lastMove.from[1]}` : null;
  const lastTo = lastMove ? `${lastMove.to[0]},${lastMove.to[1]}` : null;

  const animFromKey = animatingMove?.path?.[0]
    ? `${animatingMove.path[0][0]},${animatingMove.path[0][1]}`
    : null;
  const animToKey = animatingMove?.path?.[animatingMove.path.length - 1]
    ? `${animatingMove.path[animatingMove.path.length - 1][0]},${animatingMove.path[animatingMove.path.length - 1][1]}`
    : null;
  const capturedSet = useMemo(() => {
    const s = new Set();
    for (const [cx, cy] of capturedCells || []) s.add(`${cx},${cy}`);
    return s;
  }, [capturedCells]);

  const coachFromKey = coachFrom ? `${coachFrom[0]},${coachFrom[1]}` : null;
  const coachToKey = coachTo ? `${coachTo[0]},${coachTo[1]}` : null;

  // Build row order based on flipped
  const ys = flipped ? [...Array(SIZE).keys()] : [...Array(SIZE).keys()].reverse();
  const xs = flipped ? [...Array(SIZE).keys()].reverse() : [...Array(SIZE).keys()];

  return (
    <div className="w-full max-w-[560px] mx-auto" data-testid="game-board">
      <div className="relative rounded-lg p-4 sm:p-6 bg-wood border-2 border-[#3a2410] shadow-2xl">
        {/* Lines layer (horizontal + vertical + diagonals) — passing through cell centers (intersections) */}
        <svg
          viewBox="0 0 700 700"
          className="absolute inset-4 sm:inset-6 w-[calc(100%-2rem)] sm:w-[calc(100%-3rem)] h-[calc(100%-2rem)] sm:h-[calc(100%-3rem)] pointer-events-none"
          aria-hidden
        >
          {(() => {
            const N = SIZE;
            const pos = (i) => ((i + 0.5) * 700) / N;
            return (
              <>
                {Array.from({ length: N }).map((_, i) => {
                  const p = pos(i);
                  return (
                    <g key={`g-${i}`}>
                      <line x1={p} y1={pos(0)} x2={p} y2={pos(N - 1)} stroke="#E8E2D2" strokeOpacity="0.9" strokeWidth="1.8" />
                      <line x1={pos(0)} y1={p} x2={pos(N - 1)} y2={p} stroke="#E8E2D2" strokeOpacity="0.9" strokeWidth="1.8" />
                    </g>
                  );
                })}
                {/* Diagonals through each cell between consecutive intersections */}
                {Array.from({ length: N - 1 }).flatMap((_, ix) =>
                  Array.from({ length: N - 1 }).map((_, iy) => {
                    const x1 = pos(ix), y1 = pos(iy);
                    const x2 = pos(ix + 1), y2 = pos(iy + 1);
                    return (
                      <g key={`d-${ix}-${iy}`}>
                        <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="#E8E2D2" strokeOpacity="0.75" strokeWidth="1.2" />
                        <line x1={x2} y1={y1} x2={x1} y2={y2} stroke="#E8E2D2" strokeOpacity="0.75" strokeWidth="1.2" />
                      </g>
                    );
                  })
                )}
              </>
            );
          })()}
        </svg>

        {/* Intersections grid */}
        <div
          className="relative grid"
          style={{
            gridTemplateColumns: `repeat(${SIZE}, 1fr)`,
            gridTemplateRows: `repeat(${SIZE}, 1fr)`,
            aspectRatio: "1 / 1",
          }}
        >
          {ys.map((y) =>
            xs.map((x) => {
              const piece = board[y][x];
              const key = `${x},${y}`;
              const selected = selectedFrom && selectedFrom[0] === x && selectedFrom[1] === y;
              const isTarget = validTargets.has(key);
              const mustCap = mustCapturePieces.has(key);
              const isLastFrom = key === lastFrom;
              const isLastTo = key === lastTo;
              const isCoachFrom = key === coachFromKey;
              const isCoachTo = key === coachToKey;
              return (
                <button
                  key={key}
                  type="button"
                  data-testid={`intersection-${x}-${y}`}
                  onClick={() => onClickIntersection?.(x, y)}
                  className="relative aspect-square focus:outline-none"
                >
                  {/* Subtle highlight for last move */}
                  {(isLastFrom || isLastTo) && (
                    <span className="absolute inset-1/2 -translate-x-1/2 -translate-y-1/2 w-[60%] h-[60%] rounded-full bg-amber-400/10" />
                  )}
                  {isCoachFrom && hintsOn && (
                    <span className="absolute inset-1/2 -translate-x-1/2 -translate-y-1/2 w-[75%] h-[75%] rounded-full ring-2 ring-emerald-400/80 animate-pulse pointer-events-none" />
                  )}

                  {/* Piece */}
                  {piece && !capturedSet.has(key) && !(animatingMove && (key === animFromKey || key === animToKey)) && (
                    <Piece
                      owner={piece.owner}
                      king={piece.king}
                      themeId={pieceTheme}
                      selected={selected}
                      mustCapture={mustCap}
                      hintsOn={hintsOn}
                      data-testid={`piece-${x}-${y}-${piece.owner}${piece.king ? "-king" : ""}`}
                    />
                  )}

                  {/* Valid move dot */}
                  {isTarget && hintsOn && (
                    <span
                      data-testid={`move-dot-${x}-${y}`}
                      className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-[#f5a623] dot-pulse"
                      style={{ boxShadow: "0 0 12px rgba(245,166,35,0.9)" }}
                    />
                  )}
                  {isCoachTo && hintsOn && !isTarget && (
                    <span
                      className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-emerald-400/90 dot-pulse pointer-events-none"
                      style={{ boxShadow: "0 0 14px rgba(52,211,153,0.9)" }}
                    />
                  )}
                </button>
              );
            })
          )}
        </div>

        {/* Move overlay (capture arc animation) */}
        {animatingMove && (
          <div className="absolute inset-4 sm:inset-6 pointer-events-none">
            <MoveOverlay
              path={animatingMove.path}
              flipped={flipped}
              owner={animatingMove.owner}
              king={animatingMove.king}
              themeId={pieceTheme}
              duration={animatingMove.duration || 700}
              onDone={animatingMove.onDone}
            />
          </div>
        )}

        {/* Decorative corner glow */}
        <div className="pointer-events-none absolute -inset-1 rounded-lg ring-1 ring-amber-400/10" />
      </div>
    </div>
  );
}
